import * as THREE from 'three';
import type { InputManager } from './InputManager';
import type { PhysicsWorld } from '../physics/PhysicsWorld';
import { eventBus } from '../events/EventBus';

// ============================================
// PlayerController - Controlador del jugador FPS
// ============================================

const MOUSE_SENSITIVITY = 0.002;
const MOVE_SPEED = 5;
const RUN_MULTIPLIER = 1.8;
const JUMP_FORCE = 5;
const GRAVITY = -15;
const PLAYER_HEIGHT = 1.7;

export class PlayerController {
  private camera: THREE.PerspectiveCamera | null = null;
  private inputManager: InputManager;

  private euler = new THREE.Euler(0, 0, 0, 'YXZ');
  private velocity = new THREE.Vector3();
  private direction = new THREE.Vector3();
  private isGrounded = false;
  private canJump = true;

  private position = new THREE.Vector3(0, PLAYER_HEIGHT, 3);

  constructor(inputManager: InputManager) {
    this.inputManager = inputManager;
  }

  init(camera: THREE.PerspectiveCamera, _physics: PhysicsWorld): void {
    this.camera = camera;

    // Posición inicial
    this.camera.position.copy(this.position);

    console.log('[PlayerController] Inicializado');
  }

  update(deltaTime: number): void {
    if (!this.camera) return;

    this.handleMouseLook();
    this.handleMovement(deltaTime);
    this.updateCamera();
  }

  private handleMouseLook(): void {
    const movement = this.inputManager.getMouseMovement();

    this.euler.setFromQuaternion(this.camera!.quaternion);
    this.euler.y -= movement.x * MOUSE_SENSITIVITY;
    this.euler.x -= movement.y * MOUSE_SENSITIVITY;
    this.euler.x = Math.max(-Math.PI / 2, Math.min(Math.PI / 2, this.euler.x));

    this.camera!.quaternion.setFromEuler(this.euler);
  }

  private handleMovement(deltaTime: number): void {
    const actions = this.inputManager.getActions();

    // Calcular dirección de movimiento relativa a la cámara
    this.direction.set(0, 0, 0);

    if (actions.forward) this.direction.z -= 1;
    if (actions.backward) this.direction.z += 1;
    if (actions.left) this.direction.x -= 1;
    if (actions.right) this.direction.x += 1;

    if (this.direction.length() > 0) {
      this.direction.normalize();

      // Rotar dirección según la orientación de la cámara
      this.direction.applyQuaternion(this.camera!.quaternion);
      this.direction.y = 0;
      this.direction.normalize();
    }

    // Velocidad
    const speed = actions.run ? MOVE_SPEED * RUN_MULTIPLIER : MOVE_SPEED;

    // Aplicar movimiento
    this.velocity.x = this.direction.x * speed;
    this.velocity.z = this.direction.z * speed;

    // Gravedad
    this.velocity.y += GRAVITY * deltaTime;

    // Salto
    if (actions.jump && this.isGrounded && this.canJump) {
      this.velocity.y = JUMP_FORCE;
      this.isGrounded = false;
      this.canJump = false;
    }

    if (!actions.jump) {
      this.canJump = true;
    }

    // Actualizar posición
    this.position.x += this.velocity.x * deltaTime;
    this.position.y += this.velocity.y * deltaTime;
    this.position.z += this.velocity.z * deltaTime;

    // Colisión con el suelo
    if (this.position.y < PLAYER_HEIGHT) {
      this.position.y = PLAYER_HEIGHT;
      this.velocity.y = 0;
      this.isGrounded = true;
    }

    // Límites de la sala
    this.position.x = Math.max(-5.5, Math.min(5.5, this.position.x));
    this.position.z = Math.max(-4.5, Math.min(4.5, this.position.z));

    // Emitir evento de movimiento
    if (this.direction.length() > 0) {
      eventBus.emit('player:move', {
        x: this.position.x,
        y: this.position.y,
        z: this.position.z,
      });
    }
  }

  private updateCamera(): void {
    if (!this.camera) return;

    // Aplicar posición con leve balanceo al caminar
    const actions = this.inputManager.getActions();
    const bobSpeed = actions.run ? 12 : 8;
    const bobAmount = actions.run ? 0.06 : 0.03;

    const bobOffset = Math.sin(performance.now() * 0.001 * bobSpeed) * bobAmount;

    this.camera.position.set(
      this.position.x,
      this.position.y + bobOffset,
      this.position.z
    );
  }

  getPosition(): THREE.Vector3 {
    return this.position.clone();
  }

  getRotation(): THREE.Euler {
    return this.euler.clone();
  }
}
